import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Volume2, VolumeX, Eye, Grid3X3, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface MemoryGridGameProps {
  onBack: () => void;
}

type DifficultyLevel = 'beginner' | 'intermediate' | 'expert';
type GamePhase = 'showing' | 'input' | 'result' | 'idle';

interface GridCell {
  index: number;
  isHighlighted: boolean;
  isSelected: boolean;
  isCorrect: boolean | null;
  showOrder?: number;
}

export default function MemoryGridGame({ onBack }: MemoryGridGameProps) {
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('beginner');
  const [duration, setDuration] = useState(3);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const [grid, setGrid] = useState<GridCell[]>([]);
  const [pattern, setPattern] = useState<number[]>([]);
  const [userSelections, setUserSelections] = useState<number[]>([]);
  const [gamePhase, setGamePhase] = useState<GamePhase>('idle');
  const [currentShowingIndex, setCurrentShowingIndex] = useState(0);
  const [roundResult, setRoundResult] = useState<'correct' | 'wrong' | null>(null);
  const [correctRounds, setCorrectRounds] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);

  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
    initializeGrid();
    
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('click', initAudio);
    return () => {
      document.removeEventListener('click', initAudio);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, timeRemaining]);

  const initializeGrid = () => {
    const cells: GridCell[] = Array.from({ length: 25 }, (_, index) => ({
      index,
      isHighlighted: false,
      isSelected: false,
      isCorrect: null,
    }));
    setGrid(cells);
  };

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data !== null) {
        setBestStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const playSound = useCallback((type: 'highlight' | 'correct' | 'wrong' | 'select') => {
    if (!soundEnabled || !audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    switch (type) {
      case 'highlight':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(600, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
        break;
      case 'select':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(400, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.08);
        break;
      case 'correct':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(523, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
        break;
      case 'wrong':
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(200, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
        break;
    }
  }, [soundEnabled]);

  const getPatternCount = () => {
    switch (difficulty) {
      case 'beginner':
        return Math.floor(Math.random() * 3) + 3;
      case 'intermediate':
        return Math.floor(Math.random() * 4) + 6;
      case 'expert':
        return Math.floor(Math.random() * 4) + 6;
      default:
        return 3;
    }
  };

  const generatePattern = () => {
    const count = getPatternCount();
    const indices: number[] = [];
    while (indices.length < count) {
      const randomIndex = Math.floor(Math.random() * 25);
      if (!indices.includes(randomIndex)) {
        indices.push(randomIndex);
      }
    }
    return indices;
  };

  const startNewRound = () => {
    const newPattern = generatePattern();
    setPattern(newPattern);
    setUserSelections([]);
    setRoundResult(null);
    setCurrentShowingIndex(0);
    
    setGrid(prev => prev.map(cell => ({
      ...cell,
      isHighlighted: false,
      isSelected: false,
      isCorrect: null,
      showOrder: undefined,
    })));

    setGamePhase('showing');

    if (difficulty === 'expert') {
      showPatternSequentially(newPattern);
    } else {
      showPatternAllAtOnce(newPattern);
    }
  };

  const showPatternAllAtOnce = (patternToShow: number[]) => {
    setGrid(prev => prev.map(cell => ({
      ...cell,
      isHighlighted: patternToShow.includes(cell.index),
    })));
    patternToShow.forEach(() => playSound('highlight'));

    setTimeout(() => {
      setGrid(prev => prev.map(cell => ({
        ...cell,
        isHighlighted: false,
      })));
      setGamePhase('input');
    }, 3000);
  };

  const showPatternSequentially = (patternToShow: number[]) => {
    let currentIndex = 0;
    
    const showNext = () => {
      if (currentIndex < patternToShow.length) {
        const cellIndex = patternToShow[currentIndex];
        
        setGrid(prev => prev.map(cell => ({
          ...cell,
          isHighlighted: cell.index === cellIndex,
          showOrder: cell.index === cellIndex ? currentIndex + 1 : cell.showOrder,
        })));
        
        playSound('highlight');
        setCurrentShowingIndex(currentIndex + 1);
        currentIndex++;
        
        setTimeout(() => {
          setGrid(prev => prev.map(cell => ({
            ...cell,
            isHighlighted: false,
          })));
          setTimeout(showNext, 200);
        }, 800);
      } else {
        setGamePhase('input');
      }
    };
    
    showNext();
  };

  const handleCellClick = (index: number) => {
    if (gamePhase !== 'input') return;
    if (userSelections.includes(index)) return;

    playSound('select');
    
    const newSelections = [...userSelections, index];
    setUserSelections(newSelections);
    
    setGrid(prev => prev.map(cell => ({
      ...cell,
      isSelected: cell.index === index ? true : cell.isSelected,
    })));

    if (difficulty === 'expert') {
      const expectedIndex = pattern[newSelections.length - 1];
      if (index !== expectedIndex) {
        handleRoundEnd(false, newSelections);
        return;
      }
      if (newSelections.length === pattern.length) {
        handleRoundEnd(true, newSelections);
      }
    } else {
      if (newSelections.length === pattern.length) {
        const isCorrect = newSelections.every(sel => pattern.includes(sel)) &&
                         pattern.every(p => newSelections.includes(p));
        handleRoundEnd(isCorrect, newSelections);
      }
    }
  };

  const handleRoundEnd = (isCorrect: boolean, selections: number[]) => {
    setGamePhase('result');
    setRoundResult(isCorrect ? 'correct' : 'wrong');
    setTotalRounds(prev => prev + 1);
    
    setGrid(prev => prev.map(cell => {
      const wasSelected = selections.includes(cell.index);
      const wasInPattern = pattern.includes(cell.index);
      return {
        ...cell,
        isHighlighted: wasInPattern,
        isCorrect: wasSelected ? wasInPattern : null,
      };
    }));

    if (isCorrect) {
      playSound('correct');
      const points = difficulty === 'beginner' ? 10 : difficulty === 'intermediate' ? 20 : 30;
      setScore(prev => prev + points);
      setCorrectRounds(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      playSound('wrong');
      setStreak(0);
    }

    setTimeout(() => {
      if (timeRemaining > 0) {
        setRound(prev => prev + 1);
        startNewRound();
      }
    }, 1500);
  };

  const handleStart = () => {
    setIsActive(true);
    setTimeRemaining(duration * 60);
    setScore(0);
    setRound(1);
    setStreak(0);
    setCorrectRounds(0);
    setTotalRounds(0);
    initializeGrid();
    
    setTimeout(() => {
      startNewRound();
    }, 500);
  };

  const handleComplete = async () => {
    setIsActive(false);
    setGamePhase('idle');
    setShowResultsModal(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const accuracyVal = totalRounds > 0 ? (correctRounds / totalRounds) * 100 : 0;

      await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'mental_maths',
        duration_minutes: duration,
        difficulty_level: difficulty,
        score: score,
        accuracy: accuracyVal,
        completed_at: new Date().toISOString()
      });

      await loadStreak();
      await loadWeeklyStats();
    } catch (error) {
      console.error('Error saving memory grid session:', error);
    }
  };

  const handleCloseModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      onBack();
    }, 300);
  };

  const handlePlayAgain = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      handleStart();
    }, 300);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = totalRounds > 0 ? Math.round((correctRounds / totalRounds) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🧠</span>
            <h1 className="text-base font-bold text-gray-900">Memory Grid</h1>
          </div>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            {soundEnabled ? (
              <Volume2 size={18} className="text-gray-700" />
            ) : (
              <VolumeX size={18} className="text-gray-700" />
            )}
          </button>
        </header>

        <div className="p-4 space-y-4">
          {!isActive ? (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">🔥</span>
                    <p className="text-xs font-medium text-gray-500">Best Streak</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{bestStreak} <span className="text-sm font-medium text-gray-400">rounds</span></p>
                </div>

                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">📊</span>
                    <p className="text-xs font-medium text-gray-500">This Week</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{weeklyStats.total_sessions} <span className="text-sm font-medium text-gray-400">games</span></p>
                </div>
              </div>

              {/* Main Card */}
              <div className="bg-white rounded-2xl p-5 border border-gray-200">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-purple-500/20">
                    <Grid3X3 size={32} className="text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Memory Grid</h2>
                  <p className="text-sm text-gray-500 mt-1">Remember the pattern, test your memory!</p>
                </div>

                {/* Duration Selection */}
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Duration</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 5, 10].map((min) => (
                      <button
                        key={min}
                        onClick={() => setDuration(min)}
                        className={`py-2.5 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                          duration === min
                            ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/20'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {min}m
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Selection */}
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Difficulty</p>
                  <div className="space-y-2">
                    {([
                      { level: 'beginner' as DifficultyLevel, emoji: '🌱', label: 'Beginner', desc: '3-5 cells, shown together' },
                      { level: 'intermediate' as DifficultyLevel, emoji: '⭐', label: 'Intermediate', desc: '6-9 cells, shown together' },
                      { level: 'expert' as DifficultyLevel, emoji: '🚀', label: 'Expert', desc: '6-9 cells, shown in sequence' }
                    ]).map((item) => (
                      <button
                        key={item.level}
                        onClick={() => setDifficulty(item.level)}
                        className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all active:scale-[0.99] border-2 ${
                          difficulty === item.level
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl">{item.emoji}</span>
                        <div className="text-left flex-1">
                          <p className={`font-semibold text-sm ${difficulty === item.level ? 'text-purple-700' : 'text-gray-900'}`}>
                            {item.label}
                          </p>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                        {difficulty === item.level && (
                          <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                            <Check size={12} className="text-white" strokeWidth={3} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* How to Play */}
                <div className="bg-purple-50 rounded-xl p-4 mb-5 border border-purple-100">
                  <p className="text-xs font-bold text-purple-700 mb-2">💡 How to Play</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Watch the highlighted cells carefully</li>
                    <li>• Tap the same cells after they disappear</li>
                    <li>• Expert mode: tap in the exact sequence!</li>
                    <li>• Build streaks for bonus points</li>
                  </ul>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStart}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-purple-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <Eye size={18} />
                  Start Training
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Active Game Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Time</p>
                  <p className="text-base font-black text-gray-900">{formatTime(timeRemaining)}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Score</p>
                  <p className="text-base font-black text-gray-900">{score}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Round</p>
                  <p className="text-base font-black text-gray-900">{round}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Streak</p>
                  <p className="text-base font-black text-gray-900">{streak}🔥</p>
                </div>
              </div>

              {/* Game Phase Indicator */}
              <div className="text-center">
                {gamePhase === 'showing' && (
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-bold border border-amber-200">
                    <Eye size={16} className="animate-pulse" />
                    {difficulty === 'expert' ? `Memorize: ${currentShowingIndex}/${pattern.length}` : 'Memorize the pattern!'}
                  </div>
                )}
                {gamePhase === 'input' && (
                  <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-bold border border-blue-200">
                    <Grid3X3 size={16} />
                    {difficulty === 'expert' 
                      ? `Select in order: ${userSelections.length}/${pattern.length}` 
                      : `Select ${pattern.length} cells: ${userSelections.length}/${pattern.length}`
                    }
                  </div>
                )}
                {gamePhase === 'result' && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${
                    roundResult === 'correct' 
                      ? 'bg-green-100 text-green-700 border-green-200' 
                      : 'bg-red-100 text-red-700 border-red-200'
                  }`}>
                    {roundResult === 'correct' ? '✓ Correct!' : '✗ Wrong!'}
                  </div>
                )}
              </div>

              {/* 5x5 Grid */}
              <div className="bg-white rounded-2xl p-4 border border-gray-200">
                <div className="grid grid-cols-5 gap-2">
                  {grid.map((cell) => (
                    <button
                      key={cell.index}
                      onClick={() => handleCellClick(cell.index)}
                      disabled={gamePhase !== 'input'}
                      className={`aspect-square rounded-xl transition-all duration-200 relative overflow-hidden border-2 ${
                        cell.isHighlighted
                          ? 'bg-gradient-to-br from-purple-400 to-indigo-500 border-purple-500 shadow-lg shadow-purple-500/30 scale-95'
                          : cell.isSelected
                            ? cell.isCorrect === true
                              ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-500 shadow-lg shadow-green-500/20'
                              : cell.isCorrect === false
                                ? 'bg-gradient-to-br from-red-400 to-rose-500 border-red-500 shadow-lg shadow-red-500/20'
                                : 'bg-gradient-to-br from-blue-400 to-cyan-500 border-blue-500 shadow-lg shadow-blue-500/20'
                            : 'bg-gray-100 border-gray-200 hover:bg-gray-200 hover:border-gray-300 active:scale-95'
                      } ${gamePhase !== 'input' ? 'cursor-default' : 'cursor-pointer'}`}
                    >
                      {cell.showOrder && difficulty === 'expert' && gamePhase === 'showing' && (
                        <span className="absolute inset-0 flex items-center justify-center text-white font-black text-lg">
                          {cell.showOrder}
                        </span>
                      )}
                      {cell.isHighlighted && (
                        <div className="absolute top-1 left-1 w-3 h-3 bg-white/40 rounded-full blur-sm" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div className="text-center text-gray-500 text-xs">
                {difficulty === 'expert' 
                  ? 'Tap cells in the exact order they were shown'
                  : 'Tap all highlighted cells in any order'
                }
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results Modal */}
      {showResultsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={handleCloseModal}
        >
          <div
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">🧠</div>
                <h2 className="text-xl font-bold text-gray-900">Time's Up!</h2>
                <p className="text-sm text-gray-500">Great memory training!</p>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-purple-50 border border-purple-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">⭐</span>
                  </div>
                  <p className="text-xl font-black text-purple-600">{score}</p>
                  <p className="text-[10px] text-purple-600 font-medium">Score</p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">✓</span>
                  </div>
                  <p className="text-xl font-black text-green-600">{correctRounds}</p>
                  <p className="text-[10px] text-green-600 font-medium">Correct</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">🔥</span>
                  </div>
                  <p className="text-xl font-black text-orange-600">{bestStreak}</p>
                  <p className="text-[10px] text-orange-600 font-medium">Best Streak</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">Accuracy</span>
                  <span className="text-sm font-bold text-gray-900">{accuracy}%</span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 transition-all duration-500"
                    style={{ width: `${accuracy}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                  <span>{correctRounds} correct</span>
                  <span>{totalRounds - correctRounds} wrong</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCloseModal}
                  className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  Done
                </button>
                <button
                  onClick={handlePlayAgain}
                  className="py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-purple-500 to-indigo-600 text-white shadow-lg shadow-purple-500/20 transition-all active:scale-[0.98]"
                >
                  Play Again 🧠
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}